import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  IconCheckCircle,
  IconClose,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
  useDirection,
} from '@adc/parallax-component-library';
import type { AxiosError } from 'axios';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import { useApiError, useCareTeamConnectionError } from 'Hooks';
import { useCallback, useMemo } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { addCareTeamConnection } from 'Services/clm';
import { type RootState } from 'src/reducers';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormContainer, Input, MainContainer } from 'Components/utility';
import { useMutation } from '@tanstack/react-query';
import { notifyApp } from 'Utilities/appCommunicator';

const mapStateToProps = ({
  auth: { account_id: accountId },
  nav: {
    query: { osType, osVersion, appVersion },
  },
}: RootState) => ({
  accountId,
  osType,
  osVersion,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const validationSchema = z.object({
  clinicId: z
    .string('Enter a valid clinic ID')
    // Min 8 characters
    .min(8, 'Enter a valid clinic ID')
    // Max 16 characters
    .max(16, 'Enter a valid clinic ID')
    // Alphanumeric
    .regex(/^[a-zA-Z0-9]+$/, 'Enter a valid clinic ID'),
});

type FormValues = z.output<typeof validationSchema>;

const initialFormValues: FormValues = {
  clinicId: '',
};

const TEST_ID = 'CareTeamConnectionsForm' as const;

const CareTeamConnectionsForm = ({ accountId, osType, osVersion, appVersion }: Props) => {
  const {
    data: clinicData,
    isPending,
    isSuccess,
    mutateAsync,
  } = useMutation({
    mutationKey: ['addCareTeamConnection', accountId],
    mutationFn: async ({
      clinicId,
      userId,
      appVersion,
      osType,
      osVersion,
    }: {
      clinicId: string;
      userId: string;
      appVersion?: string;
      osType?: string;
      osVersion?: string;
    }) => {
      const response = await addCareTeamConnection({
        userId,
        clinicId,
        appVersion,
        osType,
        osVersion,
      });

      return response.clinic;
    },
    onError: (error: AxiosError<ApiErrorData>) => {
      handleApiRequestError(error);
    },
  });

  const isLtr = useDirection() === 'ltr';
  const {
    showAlreadyConnectedErrorModal,
    showAlreadyInvitedErrorModal,
    showClinicNotFoundErrorModal,
  } = useCareTeamConnectionError();
  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const onSubmitForm = async (values: FormValues) => {
    await mutateAsync({
      clinicId: values.clinicId,
      userId: accountId,
      appVersion,
      osType,
      osVersion,
    });
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    isValid,
    dirty,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: initialFormValues,
    onSubmit: onSubmitForm,
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
  });

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      switch (error.response?.data.code ?? error.response?.status) {
        case ApiStatus.ALREADY_SENT_INVITE_TO_CLINIC:
          setFieldError('clinicId', '');
          showAlreadyInvitedErrorModal();
          break;
        case ApiStatus.CONFLICT:
          setFieldError('clinicId', '');
          showAlreadyConnectedErrorModal();
          break;
        case ApiStatus.NOT_FOUND:
          setFieldError('clinicId', '');
          showClinicNotFoundErrorModal();
          break;
        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      setFieldError,
      showAlreadyConnectedErrorModal,
      showAlreadyInvitedErrorModal,
      showClinicNotFoundErrorModal,
      showApiErrorModal,
      showNetworkErrorModal,
    ]
  );

  const isFormSubmittable = values.clinicId && isValid && !isPending && dirty;

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  const handleDone = useCallback(() => {
    mediator.publish('router:navigate', '/connections');
  }, []);

  const buttonStart = useMemo(
    () =>
      isSuccess
        ? {
            element: <IconClose testID={`${TEST_ID}.back-button`} />,
            label: 'Done',
            onPress: handleClickBack,
          }
        : {
            element: isLtr ? (
              <IconArrowLeft testID={`${TEST_ID}.back-button`} />
            ) : (
              <IconArrowRight testID={`${TEST_ID}.back-button`} />
            ),
            label: 'Back',
            onPress: handleClickBack,
          },
    [handleClickBack, isSuccess, isLtr]
  );

  return (
    <MainContainer>
      {isPending && <LoadingModal aria-label="Loading" />}
      <InformationalScreen
        index={isSuccess ? 1 : 0}
        topNavigationProps={{
          title: 'Add a Care Team',
          buttonStart,
          testID: `${TEST_ID}.title`,
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <FormContainer>
              <Text marginTop={-8}>
                If your care team provided you with their Clinic ID, enter it below.
              </Text>

              <Input
                aria-label="Clinic ID"
                placeholder="Clinic ID"
                label="Clinic ID"
                errorMessage={errors.clinicId}
                isInvalid={Boolean(errors.clinicId && touched.clinicId)}
                testID={`${TEST_ID}.clinicId.input`}
                value={values.clinicId}
                onChangeText={handleChange('clinicId')}
                onBlur={handleBlur('clinicId')}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label="Connect"
              disabled={!isFormSubmittable}
              onPress={() => {
                handleSubmit();
              }}
              testID={`${TEST_ID}.submit.button`}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>

        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Stack alignContent="center" alignItems="center" marginBottom="$2">
              <IconCheckCircle
                color="$support.positive.default"
                accentColor="$neutral.noChangeWhite"
                style={{ width: 72, height: 72 }}
                testID={`${TEST_ID}.success.icon`}
              />
            </Stack>
            <Text>
              Your request to connect was sent to:
              <br />
              <Text fontWeight="bold">
                {clinicData?.name} - {clinicData?.id}
              </Text>
              <br />
              <br />
              You&apos;ll be notified when your care team accepts your request.
            </Text>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button label="Done" onPress={handleDone} testID={`${TEST_ID}.done.button`} />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CareTeamConnectionsForm);
